import { create } from 'zustand';
import { User as FirebaseUser } from 'firebase/auth';
import {
  onAuthStateChange,
  getCurrentUserRole,
  getCurrentUserAccessLevel,
  isCurrentUserActive,
  syncCurrentUserAccess,
} from '@/services/firebase';
import type { UserProfile, UserRole, UserAccessLevel } from '@/types';

interface AuthState {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  role: UserRole | null;
  accessLevel: UserAccessLevel | null;
  isActive: boolean;
  loading: boolean;
  initialized: boolean;
  setUser: (user: FirebaseUser | null) => Promise<void>;
  initialize: () => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => {
  return {
    user: null,
    userProfile: null,
    role: null,
    accessLevel: null,
    isActive: false,
    loading: true,
    initialized: false,

    initialize: () => {
      if (get().initialized) return;

      onAuthStateChange(async (user) => {
        if (user) {
          try {
            const token = await user.getIdTokenResult();
            if (!token.claims.role) {
              await syncCurrentUserAccess();
              await user.getIdToken(true);
            }
          } catch (error) {
            console.error('Failed to sync user access:', error);
          }

          const [role, accessLevel, isActive] = await Promise.all([
            getCurrentUserRole(user),
            getCurrentUserAccessLevel(user),
            isCurrentUserActive(user),
          ]);
          const { getUserProfile } = await import('@/services/firebase');
          const profile = await getUserProfile(user.uid);

          set({
            user,
            userProfile: profile,
            role,
            accessLevel,
            isActive,
            loading: false,
          });
        } else {
          set({
            user: null,
            userProfile: null,
            role: null,
            accessLevel: null,
            isActive: false,
            loading: false,
          });
        }
      });

      set({ initialized: true });
    },

    setUser: async (user: FirebaseUser | null) => {
      if (user) {
        const [role, accessLevel, isActive] = await Promise.all([
          getCurrentUserRole(user),
          getCurrentUserAccessLevel(user),
          isCurrentUserActive(user),
        ]);
        const { getUserProfile } = await import('@/services/firebase');
        const profile = await getUserProfile(user.uid);

        set({
          user,
          userProfile: profile,
          role,
          accessLevel,
          isActive,
          loading: false,
        });
      } else {
        set({
          user: null,
          userProfile: null,
          role: null,
          accessLevel: null,
          isActive: false,
          loading: false,
        });
      }
    },

    signOut: async () => {
      const { signOut } = await import('@/services/firebase');
      await signOut();
      set({
        user: null,
        userProfile: null,
        role: null,
        accessLevel: null,
        isActive: false,
      });
    },
  };
});

